import { useEffect } from "react";
import { extensionSeen, markExtensionSeen } from "@/lib/extensionPresence";

/**
 * The webapp and the browser extension, on the person's own page. Neither
 * ever posts a token: the extension signs in on its own, in a sign-in
 * window of the browser's (`apps/extension/src/auth/workos.ts`), and this
 * page's token stays in this page's script. Two things pass, both with
 * exactly `source` and `type`:
 *
 * 1. Presence. The extension's app page script says it is here on load,
 *    and again when this page asks, so the install hint is not offered
 *    to someone who has it.
 * 2. Sign out. When the person signs out here, this page says so, and the
 *    extension signs out too, then says it heard. The page waits for that,
 *    briefly, before AuthKit's sign-out takes it away.
 *
 * The shapes match `apps/extension/src/auth/apppage.ts` exactly.
 */

/** Who posts: this webapp, or the extension's app page script. */
export const WEBAPP_SOURCE = "jobenvoy-webapp";
export const EXTENSION_SOURCE = "jobenvoy-extension";

/** The extension: "I am installed." */
export const HERE = "jobenvoy:here";
/** This page: "is the extension installed?" */
export const ANYONE_HERE = "jobenvoy:anyone-here";
/** This page: "the person signed out." */
export const SIGNED_OUT = "jobenvoy:signed-out";
/** The extension: "signed out too." */
export const SIGNED_OUT_HEARD = "jobenvoy:signed-out-heard";

/** How long a sign-out waits for the extension to say it heard. */
export const HEARD_TIMEOUT_MS = 1_000;

export type WebappMessage = {
    source: typeof WEBAPP_SOURCE;
    type: typeof ANYONE_HERE | typeof SIGNED_OUT;
};

/** The window the bridge runs in, as much as it uses. */
export interface PageWindow {
    location: { origin: string };
    addEventListener(type: "message", listener: (event: MessageEvent) => void): void;
    removeEventListener(type: "message", listener: (event: MessageEvent) => void): void;
    postMessage(message: unknown, targetOrigin: string): void;
}

/** What the listener reads of a message event. */
export interface PageMessage {
    origin: string;
    source: unknown;
    data: unknown;
}

/** One of this page's messages to the extension. */
export function webappMessage(type: WebappMessage["type"]): WebappMessage {
    return { source: WEBAPP_SOURCE, type };
}

/**
 * What the extension said, or `null` when the message is not the
 * extension's: another origin or window, another shape, or a type this
 * does not know. A message with a token in it is not one.
 */
export function extensionSaid(
    event: PageMessage,
    page: PageWindow,
): typeof HERE | typeof SIGNED_OUT_HEARD | null {
    if (event.origin !== page.location.origin || event.source !== page) {
        return null;
    }
    const data = event.data;
    if (typeof data !== "object" || data === null || Array.isArray(data)) {
        return null;
    }
    if (Object.keys(data).sort().join() !== "source,type") {
        return null;
    }
    const message = data as { source: unknown; type: unknown };
    if (message.source !== EXTENSION_SOURCE) {
        return null;
    }
    return message.type === HERE || message.type === SIGNED_OUT_HEARD ? message.type : null;
}

/** Posts one of this page's messages to its own window, for its own origin only. */
export function tell(page: PageWindow, type: WebappMessage["type"]): void {
    page.postMessage(webappMessage(type), page.location.origin);
}

/**
 * Listens for the extension saying it is here, and asks once, in case it
 * said so before this listened. Returns a stop.
 */
export function listenForExtension(page: PageWindow): () => void {
    const listener = (event: MessageEvent): void => {
        if (extensionSaid(event, page) === HERE) {
            markExtensionSeen();
        }
    };
    page.addEventListener("message", listener);
    tell(page, ANYONE_HERE);
    return () => page.removeEventListener("message", listener);
}

/**
 * Tells the extension the person signed out, and resolves once it says it
 * heard, or after `timeoutMs`. With no extension seen there is nobody to
 * wait for: it still says so, and resolves at once.
 */
export function tellExtensionSignedOut(
    page: PageWindow,
    options: { timeoutMs?: number; present?: () => boolean } = {},
): Promise<void> {
    const { timeoutMs = HEARD_TIMEOUT_MS, present = extensionSeen } = options;
    return new Promise((resolve) => {
        if (!present()) {
            tell(page, SIGNED_OUT);
            resolve();
            return;
        }
        let cancel = (): void => {};
        const finish = (): void => {
            page.removeEventListener("message", listener);
            cancel();
            resolve();
        };
        const listener = (event: MessageEvent): void => {
            if (extensionSaid(event, page) === SIGNED_OUT_HEARD) finish();
        };
        page.addEventListener("message", listener);
        const timer = setTimeout(finish, timeoutMs);
        cancel = () => clearTimeout(timer);
        tell(page, SIGNED_OUT);
    });
}

/** Presence for the whole app: listens from the first render, for the page's life. */
export function useExtensionPresence(): void {
    useEffect(() => listenForExtension(window), []);
}
